import React from 'react';

import {
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    type AdminArtifactRendererProps,
    citationsFromRecord,
    JsonValue,
    MAX_TRACE_TIMELINE_ENTRIES,
    numberValue,
    renderUntrustedString,
    scalarString,
    stringsFromUnknown,
    stringValue,
    toRecord,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';

export type TraceTimelineEntry = {
    readonly source: string;
    readonly atIso: string | null;
    readonly label: string;
    readonly fact: string;
    readonly citation: string;
    readonly eventOffset: number | null;
    readonly eventType: string | null;
    readonly toolName: string | null;
    readonly status: string | null;
    readonly role: string | null;
    readonly textExcerpt: string | null;
    readonly errorExcerpt: string | null;
    readonly inputTokens: number | null;
    readonly outputTokens: number | null;
    readonly costUsd: string | null;
};

export type TraceAnnotation = {
    readonly kind: string;
    readonly title: string;
    readonly summary: string;
    readonly severity: string;
    readonly confidence: string | null;
    readonly evidence: ReadonlyArray<string>;
    readonly citations: ReadonlyArray<string>;
    readonly thresholds: ReadonlyArray<string>;
    readonly caveats: ReadonlyArray<string>;
};

export function TraceTimelineArtifact({
    artifact,
}: AdminArtifactRendererProps) {
    const timeline = getTraceTimelineData(artifact.payload);
    return (
        <ArtifactFrame
            artifact={artifact}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            <ArtifactEvidence artifact={artifact} />
            <p className="text-muted-foreground text-[11px]">
                <span>Trace timeline</span>
                <span className="text-muted-foreground/40">{' · '}</span>
                <span>
                    {timeline.entries.length}{' '}
                    {timeline.entries.length === 1 ? 'entry' : 'entries'}
                </span>
                {timeline.sourceTruncated ? (
                    <React.Fragment>
                        <span className="text-muted-foreground/40">
                            {' · '}
                        </span>
                        <span>Timeline truncated</span>
                    </React.Fragment>
                ) : null}
                {timeline.uiTruncated ? (
                    <React.Fragment>
                        <span className="text-muted-foreground/40">
                            {' · '}
                        </span>
                        <span>Showing first {MAX_TRACE_TIMELINE_ENTRIES}</span>
                    </React.Fragment>
                ) : null}
            </p>
            <TraceAnnotationList
                annotations={timeline.annotations}
                truncated={timeline.annotationsTruncated}
            />
            {timeline.entries.length > 0 ? (
                <ol className="border-border/60 ml-[3px] border-l">
                    {timeline.entries.map((entry, index) => (
                        <li
                            key={traceTimelineEntryKey(entry, index)}
                            className="relative py-2 pl-4"
                        >
                            <span className="bg-muted-foreground/40 absolute top-[15px] -left-[2.5px] size-1 rounded-full" />
                            <div className="grid gap-x-4 gap-y-1 md:grid-cols-[6.5rem_1fr]">
                                <div
                                    className="text-muted-foreground/70 pt-px font-mono text-[11px] tabular-nums"
                                    title={entry.atIso ?? undefined}
                                >
                                    {formatTimelineTime(entry.atIso)}
                                </div>
                                <div className="min-w-0 space-y-1">
                                    <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
                                        <span className="text-foreground min-w-0 text-xs font-medium break-words">
                                            {renderUntrustedString(entry.label)}
                                        </span>
                                        <span className="text-muted-foreground/70 text-[11px]">
                                            <span>
                                                {formatTimelineSource(
                                                    entry.source,
                                                )}
                                            </span>
                                            {entry.status ? (
                                                <React.Fragment>
                                                    <span className="text-muted-foreground/40">
                                                        {' · '}
                                                    </span>
                                                    <span>{entry.status}</span>
                                                </React.Fragment>
                                            ) : null}
                                        </span>
                                    </div>
                                    <p className="text-muted-foreground text-xs leading-relaxed">
                                        {renderUntrustedString(entry.fact)}
                                    </p>
                                    <TraceTimelineMetadata entry={entry} />
                                    <TraceTimelineTokenSummary entry={entry} />
                                    <TraceTimelineExcerpt
                                        label="Text"
                                        value={entry.textExcerpt}
                                    />
                                    <TraceTimelineExcerpt
                                        label="Error"
                                        value={entry.errorExcerpt}
                                        tone="destructive"
                                    />
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>
            ) : (
                <p className="text-muted-foreground text-xs">
                    No timeline entries were included in this artifact.
                </p>
            )}
            <details className="group">
                <summary className="text-muted-foreground hover:text-foreground cursor-pointer text-xs transition-colors">
                    Raw JSON payload
                </summary>
                <div className="border-border/60 mt-1.5 border-l pl-3">
                    <JsonValue value={artifact.payload} />
                </div>
            </details>
        </ArtifactFrame>
    );
}

export function TraceAnnotationList({
    annotations,
    truncated = false,
}: {
    readonly annotations: ReadonlyArray<TraceAnnotation>;
    readonly truncated?: boolean;
}) {
    if (annotations.length === 0 && !truncated) {
        return null;
    }
    return (
        <div className="space-y-1.5">
            <h5 className="text-foreground text-xs font-medium">
                Trace annotations
            </h5>
            {annotations.length > 0 ? (
                <div className="space-y-1.5">
                    {annotations.map((annotation, index) => (
                        <TraceAnnotationRow
                            key={traceAnnotationKey(annotation, index)}
                            annotation={annotation}
                        />
                    ))}
                </div>
            ) : null}
            {truncated ? (
                <p className="text-muted-foreground text-[11px]">
                    Additional annotations were omitted from this view.
                </p>
            ) : null}
        </div>
    );
}

function TraceAnnotationRow({
    annotation,
}: {
    readonly annotation: TraceAnnotation;
}) {
    const primaryCaveat = annotation.caveats[0] ?? null;
    const metadata = [
        formatAnnotationKind(annotation.kind),
        annotation.severity,
        annotation.confidence ? `${annotation.confidence} confidence` : null,
    ].filter((value): value is string => Boolean(value));
    return (
        <div className="border-border/70 bg-background/70 rounded-md border px-3 py-2">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-foreground min-w-0 text-xs font-medium break-words">
                    {renderUntrustedString(annotation.title)}
                </span>
                {metadata.length > 0 ? (
                    <span className="text-muted-foreground/70 text-[11px]">
                        {metadata.join(' · ')}
                    </span>
                ) : null}
            </div>
            <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                {renderUntrustedString(annotation.summary)}
            </p>
            {annotation.evidence.length > 0 ? (
                <p className="text-muted-foreground mt-1 text-[11px] leading-relaxed break-words">
                    <span className="text-muted-foreground/70">Evidence: </span>
                    {annotation.evidence.map((item, index) => (
                        <React.Fragment key={`${item}-${index}`}>
                            {index > 0 ? ', ' : null}
                            {renderUntrustedString(item)}
                        </React.Fragment>
                    ))}
                </p>
            ) : null}
            {primaryCaveat ? (
                <p className="text-muted-foreground/80 mt-1 text-[11px] leading-relaxed">
                    {renderUntrustedString(primaryCaveat)}
                </p>
            ) : null}
            {annotation.citations.length > 0 ? (
                <p className="text-muted-foreground/70 mt-1 font-mono text-[11px] break-all">
                    {annotation.citations.slice(0, 4).join(', ')}
                </p>
            ) : null}
        </div>
    );
}

function TraceTimelineMetadata({
    entry,
}: {
    readonly entry: TraceTimelineEntry;
}) {
    const metadata: Array<{ readonly label: string; readonly value: string }> =
        [];
    if (entry.role) {
        metadata.push({ label: 'role', value: entry.role });
    }
    if (entry.toolName) {
        metadata.push({ label: 'tool', value: entry.toolName });
    }
    if (entry.eventType) {
        metadata.push({ label: 'event', value: entry.eventType });
    }
    if (entry.eventOffset !== null) {
        metadata.push({ label: 'offset', value: String(entry.eventOffset) });
    }
    if (entry.citation) {
        metadata.push({ label: 'citation', value: entry.citation });
    }
    if (metadata.length === 0) {
        return null;
    }
    return (
        <dl className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
            {metadata.map((item) => (
                <div key={item.label} className="flex min-w-0 gap-1">
                    <dt className="text-muted-foreground">{item.label}</dt>
                    <dd className="min-w-0 break-all">
                        {renderUntrustedString(item.value)}
                    </dd>
                </div>
            ))}
        </dl>
    );
}

function TraceTimelineTokenSummary({
    entry,
}: {
    readonly entry: TraceTimelineEntry;
}) {
    const tokens: Array<string> = [];
    if (entry.inputTokens !== null) {
        tokens.push(`Input ${formatTokenCount(entry.inputTokens)}`);
    }
    if (entry.outputTokens !== null) {
        tokens.push(`Output ${formatTokenCount(entry.outputTokens)}`);
    }
    if (entry.costUsd) {
        tokens.push(`Cost ${formatCostUsd(entry.costUsd)}`);
    }
    if (tokens.length === 0) {
        return null;
    }
    return (
        <p className="text-muted-foreground text-[11px] tabular-nums">
            {tokens.map((token, index) => (
                <React.Fragment key={token}>
                    {index > 0 ? (
                        <span className="text-muted-foreground/40">
                            {' · '}
                        </span>
                    ) : null}
                    <span>{token}</span>
                </React.Fragment>
            ))}
        </p>
    );
}

function TraceTimelineExcerpt({
    label,
    value,
    tone = 'default',
}: {
    readonly label: string;
    readonly value: string | null;
    readonly tone?: 'default' | 'destructive';
}) {
    if (!value) {
        return null;
    }
    return (
        <div
            className={
                tone === 'destructive'
                    ? 'border-l border-red-200 pl-2.5 text-xs'
                    : 'border-border border-l pl-2.5 text-xs'
            }
        >
            <span
                className={
                    tone === 'destructive'
                        ? 'text-[11px] font-medium text-red-700'
                        : 'text-muted-foreground/70 text-[11px]'
                }
            >
                {label}
            </span>
            <div className="text-muted-foreground break-words">
                {renderUntrustedString(value)}
            </div>
        </div>
    );
}

function getTraceTimelineData(payload: unknown): {
    readonly entries: ReadonlyArray<TraceTimelineEntry>;
    readonly annotations: ReadonlyArray<TraceAnnotation>;
    readonly annotationsTruncated: boolean;
    readonly sourceTruncated: boolean;
    readonly uiTruncated: boolean;
} {
    const entries: Array<TraceTimelineEntry> = [];
    const annotations: Array<TraceAnnotation> = [];
    let annotationsTruncated = false;
    let sourceTruncated = false;
    let uiTruncated = false;

    const appendFromArray = (items: unknown) => {
        if (!Array.isArray(items)) {
            return;
        }
        for (const item of items) {
            if (entries.length >= MAX_TRACE_TIMELINE_ENTRIES) {
                uiTruncated = true;
                break;
            }
            const entry = traceTimelineEntryFromRecord(item);
            if (entry) {
                entries.push(entry);
            }
        }
    };

    const appendAnnotations = (items: unknown) => {
        for (const annotation of getTraceAnnotations({ annotations: items })) {
            if (
                annotations.some(
                    (existing) =>
                        traceAnnotationKey(existing, 0) ===
                        traceAnnotationKey(annotation, 0),
                )
            ) {
                continue;
            }
            annotations.push(annotation);
        }
    };

    const appendFromContainer = (container: unknown) => {
        const record = toRecord(container);
        sourceTruncated =
            sourceTruncated ||
            record['timeline_truncated'] === true ||
            record['truncated'] === true;
        annotationsTruncated =
            annotationsTruncated || record['annotations_truncated'] === true;
        appendAnnotations(record['annotations']);
        appendFromArray(record['timeline']);
        appendFromArray(record['entries']);
        appendFromArray(record['events']);
    };

    appendFromContainer(payload);
    if (entries.length > 0) {
        return {
            entries,
            annotations,
            annotationsTruncated,
            sourceTruncated,
            uiTruncated,
        };
    }

    const record = toRecord(payload);
    appendFromContainer(record['trace']);
    if (entries.length > 0) {
        return {
            entries,
            annotations,
            annotationsTruncated,
            sourceTruncated,
            uiTruncated,
        };
    }

    const traces = record['traces'];
    if (Array.isArray(traces)) {
        for (const [traceIndex, trace] of traces.entries()) {
            appendFromContainer(trace);
            if (
                entries.length >= MAX_TRACE_TIMELINE_ENTRIES &&
                traceIndex < traces.length - 1
            ) {
                uiTruncated = true;
                break;
            }
        }
    }

    return {
        entries,
        annotations,
        annotationsTruncated,
        sourceTruncated,
        uiTruncated,
    };
}

export function getTraceAnnotations(
    payload: unknown,
): ReadonlyArray<TraceAnnotation> {
    const value = toRecord(payload)['annotations'];
    if (!Array.isArray(value)) {
        return [];
    }
    return value.flatMap((item) => {
        const annotation = traceAnnotationFromRecord(item);
        return annotation ? [annotation] : [];
    });
}

function traceAnnotationFromRecord(value: unknown): TraceAnnotation | null {
    const record = toRecord(value);
    const title = stringValue(record['title']);
    const summary = stringValue(record['summary']);
    if (!title || !summary) {
        return null;
    }
    return {
        kind: stringValue(record['kind']) ?? 'annotation',
        title,
        summary,
        severity: stringValue(record['severity']) ?? 'info',
        confidence: scalarString(record['confidence']),
        evidence: stringsFromUnknown(record['evidence']),
        citations: citationsFromRecord(record),
        thresholds: stringsFromUnknown(record['thresholds']),
        caveats: stringsFromUnknown(record['caveats']),
    };
}

function traceTimelineEntryFromRecord(
    value: unknown,
): TraceTimelineEntry | null {
    const record = toRecord(value);
    const source = stringValue(record['source']) ?? 'unknown';
    const label = stringValue(record['label']) ?? source;
    const fact = stringValue(record['fact']);
    const citation = stringValue(record['citation']) ?? 'unknown citation';
    if (!fact) {
        return null;
    }
    return {
        source,
        atIso: stringValue(record['at_iso']),
        label,
        fact,
        citation,
        eventOffset: numberValue(record['event_offset']),
        eventType: stringValue(record['event_type']),
        toolName: stringValue(record['tool_name']),
        status: stringValue(record['status']),
        role: stringValue(record['role']),
        textExcerpt: stringValue(record['text_excerpt']),
        errorExcerpt: stringValue(record['error_excerpt']),
        inputTokens: numberValue(record['input_tokens']),
        outputTokens: numberValue(record['output_tokens']),
        costUsd: stringValue(record['cost_usd']),
    };
}

function traceTimelineEntryKey(
    entry: TraceTimelineEntry,
    index: number,
): string {
    return `${entry.citation}-${entry.atIso ?? 'unknown'}-${index}`;
}

function traceAnnotationKey(
    annotation: TraceAnnotation,
    index: number,
): string {
    return [
        annotation.kind,
        annotation.title,
        annotation.citations[0] ?? 'uncited',
        index,
    ].join(':');
}

function formatAnnotationKind(kind: string): string {
    return kind
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function formatTimelineSource(source: string): string {
    const label = source.split('_').filter(Boolean).join(' ');
    if (!label) {
        return 'Unknown';
    }
    return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatTimelineTime(value: string | null): string {
    if (!value) {
        return 'Unknown time';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
    });
}

function formatTokenCount(value: number): string {
    return value.toLocaleString('en-US');
}

function formatCostUsd(value: string): string {
    return value.startsWith('$') ? value : `$${value}`;
}
